import { useState, useEffect } from "react";
import "./MonthlyDashboard.css";

const categoryGoals = {
  Work: ["Deliver project milestone", "Improve performance", "Expand documentation", "Team training", "Process improvement", "Quality assurance"],
  Learning: ["Complete course module", "Build practice project", "Master new concept", "Coding challenges", "Contribute to open source", "Documentation writing"],
  SelfDevelopment: ["Read 2 books", "Maintain meditation streak", "Network with  5 professionals", "Weekly reflections", "Learn new skill", "Work-life balance"],
  Fitness: ["Lose 2kg body fat", "Increase strength", "Run 100km total", "Improve flexibility", "Build muscle mass", "Improve endurance"]
};

export default function MonthlyDashboard() {
  const [goal, setGoal] = useState({
    category: "Work",
    month: "",
    goals: "",
    completion: 0
  });
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState("All");

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    fetch("http://localhost:5000/api/monthly", {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setGoals(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGoal({ ...goal, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setGoal({ ...goal, category, goals: "", completion: 0 });
  };

  const handleSliderChange = (e) => setGoal({ ...goal, completion: e.target.value });

  const handleSubmit = async () => {
    if (!goal.month || !goal.goals) {
      alert("Please fill in required fields (Month, Goal)");
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch("http://localhost:5000/api/monthly", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(goal),
      });
      const data = await res.json();
      setGoals([...goals, data]);
      setGoal({ category: "Work", month: "", goals: "", completion: 0 });
      setShowForm(false);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredGoals = filterCategory === "All" ? goals : goals.filter(g => g.category === filterCategory);

  return (
    <div className="monthly-dashboard-container">
      <div className="monthly-header">
        <h2>🎯 Monthly Goals</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ New Goal"}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Create Monthly Goal</h3>

          {/* Category Selection */}
          <div className="form-group">
            <label>Category *</label>
            <div className="category-buttons">
              {Object.keys(categoryGoals).map(cat => (
                <button
                  key={cat}
                  className={`category-btn ${goal.category === cat ? "active" : ""}`}
                  onClick={() => handleCategoryChange({ target: { value: cat } })}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Month *</label>
              <input
                type="month"
                name="month"
                value={goal.month}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Goal *</label>
              <select name="goals" value={goal.goals} onChange={handleChange}>
                <option value="">Select goal...</option>
                {categoryGoals[goal.category].map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Progress Completion: <strong>{goal.completion}%</strong></label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="100"
                value={goal.completion}
                onChange={handleSliderChange}
                className="slider"
              />
              <div className="completion-display">
                <div className="completion-bar" style={{ width: `${goal.completion}%` }}></div>
              </div>
            </div>
            <div className="percentage-buttons">
              {[0, 25, 50, 75, 100].map(pct => (
                <button
                  key={pct}
                  className={`quick-pct ${goal.completion === pct ? "selected" : ""}`}
                  onClick={() => setGoal({ ...goal, completion: pct })}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          <button className="btn-submit" onClick={handleSubmit}>Save Goal</button>
        </div>
      )}

      <div className="filter-section">
        <label>Filter by Category:</label>
        <div className="filter-buttons">
          {["All", "Work", "Learning", "SelfDevelopment", "Fitness"].map(cat => (
            <button
              key={cat}
              className={`filter-btn ${filterCategory === cat ? "active" : ""}`}
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="goals-section">
        <h3>Monthly Goals ({filteredGoals.length})</h3>
        {filteredGoals.length === 0 ? (
          <p className="empty-state">No goals set yet. Create your first monthly goal!</p>
        ) : (
          <div className="goals-grid">
            {filteredGoals.map((g, i) => (
              <div key={i} className={`goal-card category-${g.category?.toLowerCase() || "work"}`}>
                <div className="goal-header">
                  <span className="badge">{g.category || "Work"}</span>
                  <span className="month">{new Date(g.month + "-01").toLocaleString("default", { month: "long", year: "numeric" })}</span>
                </div>
                <h4>{g.goals}</h4>
                <div className="goal-progress">
                  <div className="progress-bar-container">
                    <div className="progress-bar-fill" style={{ width: `${g.completion}%` }}></div>
                  </div>
                  <div className="progress-text">
                    <span className="percentage">{g.completion}%</span>
                    <span className="label">Complete</span>
                  </div>
                </div>
                <div className="status-badge" style={{ backgroundColor: getStatusColor(g.completion) }}>
                  {getStatusLabel(g.completion)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getStatusColor(completion) {
  if (completion >= 80) return "#27ae60";
  if (completion >= 50) return "#f39c12";
  if (completion >= 25) return "#e67e22";
  return "#e74c3c";
}

function getStatusLabel(completion) {
  if (completion >= 80) return "On Track ✓";
  if (completion >= 50) return "In Progress";
  if (completion >= 25) return "Starting";
  return "Not Started";
}